import { useLocalStore } from 'mobx-react-lite';

import { IDevice, IDeviceSession } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { DeviceStatus, IUserDevicesItem } from 'APP/model/userSettings/userSettingsModel.types';
import Entities from 'STORE';

import { matchPlatformNameToDevicePlatform } from '../utils/matchPlatformNameToPlatform';

export interface ISessionsItemDetailsPopupParams {
  sessionKey: string;
}

interface ISessionsItemDetailsPopupPresenter {
  hasBackButton: boolean;
  session: IUserDevicesItem | null;
  device: IDevice | null;
  deviceSession: IDeviceSession;
  removeSession(sessionKey: string[]): void;
  terminateDevice(): Promise<void>;
  blockDevice(): Promise<void>;
  unblockDevice(): Promise<void>;
  onClose(): void;
  onBack(): void;
}

export function useSessionsItemDetailsPopupPresenter({
  popupInstance,
  params,
  onClose,
  onBack,
}: ICommonPopupsProps<ISessionsItemDetailsPopupParams>): ISessionsItemDetailsPopupPresenter {
  const sessionKey = params!.sessionKey;
  const { t } = useTranslation();
  const presenter = useLocalStore<ISessionsItemDetailsPopupPresenter>(() => ({
    get session(): IUserDevicesItem | null {
      return Entities.sessionsStore.getSessionByKey(sessionKey);
    },

    get device(): IDevice | null {
      const device = presenter.session?.device;
      return device
        ? {
            deviceId: device.deviceId,
            device: device.device,
            osName: device.osName,
            osVersion: device.osVersion,
            appVersion: device.appVersion,
            platform: matchPlatformNameToDevicePlatform(device.platform),
          }
        : null;
    },

    get deviceSession(): IDeviceSession {
      return {
        lastActivityTs: presenter.session?.lastActivityTs || 0,
      };
    },

    get hasBackButton(): boolean {
      return !!onBack;
    },

    removeSession(sessionKey: string[]): void {
      Entities.sessionsStore.removeSessions(sessionKey);
    },

    async terminateDevice(): Promise<void> {
      const confirmation = await Tasks.app.showConfirm({
        text: t('trusted_device_delete_device_confirm_title'),
      });

      if (!confirmation) {
        return;
      }

      const response = await Tasks.userSettings.terminateOrBlockSessions(
        [sessionKey],
        DeviceStatus.Deleted
      );

      if (response) {
        presenter.onBack();
        presenter.removeSession([sessionKey]);
      } else {
        Tasks.app.showToast(t('something_went_wrong'));
      }
    },

    async blockDevice(): Promise<void> {
      const confirmation = await Tasks.app.showConfirm({
        text: t('trusted_device_block_device_confirm_description'),
      });

      if (!confirmation) {
        return;
      }

      const response = await Tasks.userSettings.terminateOrBlockSessions(
        [sessionKey],
        DeviceStatus.Blocked
      );

      if (response) {
        presenter.onBack();
      } else {
        Tasks.app.showToast(t('something_went_wrong'));
      }
    },

    async unblockDevice(): Promise<void> {
      const confirmation = await Tasks.app.showConfirm({
        title: t('trusted_device_unblock_device_confirm_title'),
        text: t('trusted_device_unblock_device_confirm_description'),
      });

      if (!confirmation) {
        return;
      }

      const response = await Tasks.userSettings.setDeviceStatus(sessionKey, DeviceStatus.Unlocked);

      if (response) {
        presenter.onBack();
        Tasks.app.showToast(t('trusted_device_unblocked_finish_login_to_complete'));
      } else {
        Tasks.app.showToast(t('something_went_wrong'));
      }
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    onBack(): void {
      popupInstance.close();
      onBack?.();
    },
  }));

  return presenter;
}
